/**
 * VenuesTableView — virtualized DataGrid for the Venues page.
 *
 * Why this component exists: at thousands of theaters the chain/state
 * card layouts collapse under their own weight (every row sits in the
 * DOM at once, hundreds of accordion headers, no sortable columns).
 * The DataGrid renders only visible rows, exposes column sort/filter,
 * and surfaces a row-selection checkbox column we hook into a bulk
 * "Send request form to N venues" action.
 *
 * Inputs are the same filteredVenues array the card view consumes —
 * page-level filters apply uniformly. Selection state is owned by the
 * page so the bulk action bar can read it from above the grid.
 */
import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Chip, Typography, Tooltip, IconButton } from '@mui/material';
import {
  Edit as EditIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { formatRelativeTime } from '../utils/library';


function _certSummary(screens) {
  const total = (screens || []).length;
  if (total === 0) return { label: 'No screens', count: 0, total: 0, kind: 'none' };
  const withCert = screens.filter(s => s.certificate_filename).length;
  if (withCert === 0) return { label: `0/${total}`, count: 0, total, kind: 'missing' };
  if (withCert === total) return { label: `${withCert}/${total}`, count: withCert, total, kind: 'complete' };
  return { label: `${withCert}/${total}`, count: withCert, total, kind: 'partial' };
}


export default function VenuesTableView({
  venues,
  selectionModel,
  onSelectionChange,
  onEdit,
  onSendRequest,
}) {
  const columns = React.useMemo(() => ([
    {
      field: 'name',
      headerName: 'Venue',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', lineHeight: 1.2 }}>
          <Typography variant="body2" fontWeight={600}>{params.row.name}</Typography>
          {params.row.chain && (
            <Typography variant="caption" color="text.secondary">{params.row.chain}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'city_state',
      headerName: 'Location',
      width: 160,
      valueGetter: (_, row) => [row.city, row.state].filter(Boolean).join(', ') || '—',
    },
    {
      field: 'screens_count',
      headerName: 'Screens',
      type: 'number',
      width: 90,
      valueGetter: (_, row) => (row.screens || []).length,
    },
    {
      field: 'cert_status',
      headerName: 'Certs',
      width: 110,
      sortComparator: (a, b) => (a?.count ?? 0) - (b?.count ?? 0),
      valueGetter: (_, row) => _certSummary(row.screens),
      renderCell: (params) => {
        const s = params.value;
        const color = s.kind === 'complete' ? 'success'
          : s.kind === 'partial' ? 'warning'
          : s.kind === 'missing' ? 'error' : 'default';
        return <Chip size="small" label={s.label} color={color} variant="outlined" />;
      },
    },
    {
      field: 'preferred_format',
      headerName: 'Format',
      width: 110,
      valueGetter: (_, row) => row.preferred_format || '—',
    },
    {
      field: 'kdm_method_preferred',
      headerName: 'KDM via',
      width: 110,
      valueGetter: (_, row) => row.kdm_method_preferred || '—',
    },
    {
      field: 'tech_contact_name',
      headerName: 'Tech Contact',
      flex: 0.8,
      minWidth: 180,
      renderCell: (params) => {
        const name = params.row.tech_contact_name;
        const email = params.row.tech_contact_email;
        if (!name && !email) return <Typography variant="caption" color="text.disabled">—</Typography>;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', lineHeight: 1.2, overflow: 'hidden' }}>
            {name && <Typography variant="body2" noWrap>{name}</Typography>}
            {email && <Typography variant="caption" color="text.secondary" noWrap>{email}</Typography>}
          </Box>
        );
      },
      valueGetter: (_, row) => row.tech_contact_name || row.tech_contact_email || '',
    },
    {
      field: 'last_kdm_at',
      headerName: 'Last KDM',
      width: 130,
      valueGetter: (_, row) => row.last_kdm_at || null,
      renderCell: (params) => {
        if (!params.value) return <Typography variant="caption" color="text.disabled">—</Typography>;
        return (
          <Tooltip title={new Date(params.value).toLocaleString()}>
            <Typography variant="caption">{formatRelativeTime(params.value)}</Typography>
          </Tooltip>
        );
      },
      sortComparator: (a, b) => {
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return new Date(a).getTime() - new Date(b).getTime();
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="Edit venue">
            <IconButton size="small" onClick={() => onEdit?.(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Send request form to this venue">
            <IconButton size="small" onClick={() => onSendRequest?.(params.row)}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]), [onEdit, onSendRequest]);

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={venues}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onSelectionChange}
        getRowId={(r) => r.id}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
        }}
        pageSizeOptions={[25, 50, 100, 250]}
        density="compact"
        rowHeight={56}
        sx={{
          // Virtualization is on by default in MUI x-data-grid v8.
          // We keep the height bounded so the toolbar + pagination row stay
          // anchored; the grid scrolls within.
          height: 'calc(100vh - 320px)',
          minHeight: 480,
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
        }}
      />
    </Box>
  );
}
